/**
 * useTableActions — state and handlers for table management actions.
 * Manages: split bill, item/all transfer, cancel, payment, discount, invoice, close.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { formatPrice } from "@/utils/currency";
import { submitTableSettlement, tablePaymentError as toErrMsg } from "@/utils/tableSettlement";

export function useTableActions({ authAxios, table, account, fetchAccount, onRefresh, onBack }) {
  const { t } = useTranslation();

  const total = account?.total || 0;
  const pending = account?.pending || 0;
  const orders = account?.orders || [];

  // === DIALOG VISIBILITY ===
  const [showSplit, setShowSplit] = useState(false);
  const [showTransferItem, setShowTransferItem] = useState(false);
  const [showTransferAll, setShowTransferAll] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showDiscount, setShowDiscount] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);

  // === LAST PAYMENT (for print receipt prompt) ===
  const [lastPaymentResult, setLastPaymentResult] = useState(null); // { payment_id, orders_released }

  // === SPLIT STATE ===
  const [splitMode, setSplitMode] = useState("persons");
  const [splitPersons, setSplitPersons] = useState(2);
  const [splitResult, setSplitResult] = useState(null);
  const [selectedSplitItems, setSelectedSplitItems] = useState([]);

  // === TRANSFER STATE ===
  const [selectedTransferItems, setSelectedTransferItems] = useState([]);
  const [targetTableId, setTargetTableId] = useState("");

  // === PAYMENT STATE ===
  const [paymentMethod, setPaymentMethod] = useState("");
  const [cardType, setCardType] = useState("");
  const [cashReceived, setCashReceived] = useState("");
  const [needsChange, setNeedsChange] = useState(false); // false = pagamento exato (padrão)
  const [paymentAmount, setPaymentAmount] = useState("");
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentMode, setPaymentMode] = useState("single");
  const [partialPayments, setPartialPayments] = useState([]);
  const [currentPartialAmount, setCurrentPartialAmount] = useState("");

  // === DISCOUNT STATE ===
  const [discountType, setDiscountType] = useState("percentage");
  const [discountValue, setDiscountValue] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(0);

  // === INVOICE STATE ===
  const [processingInvoice, setProcessingInvoice] = useState(false);

  // === COMPUTED ===
  const finalTotal = Math.max(0, total - appliedDiscount);
  const totalPartialPaid = partialPayments.reduce((sum, p) => sum + p.amount, 0);
  const remainingToPay = Math.max(0, finalTotal - totalPartialPaid);
  const changeAmount = paymentMethod === "cash" && cashReceived
    ? Math.max(0, parseFloat(cashReceived) - (paymentMode === "single" ? finalTotal : remainingToPay))
    : 0;
  const partialAmount = parseFloat(currentPartialAmount) || 0;
  const partialCashChange = paymentMethod === "cash" && cashReceived
    ? Math.max(0, parseFloat(cashReceived) - partialAmount)
    : 0;

  // === HANDLERS ===

  const handleCancel = async () => {
    try {
      await authAxios.post(`/tables/${table.id}/cancel`);
      toast.success("Mesa cancelada (registros guardados)");
      setShowCancel(false);
      await onRefresh();
      onBack();
    } catch { toast.error("Error al cancelar"); }
  };

  const handleSplitCalc = async () => {
    try {
      if (splitMode === "persons") {
        const res = await authAxios.post(`/tables/${table.id}/split-bill`, { mode: "persons", num_persons: splitPersons });
        setSplitResult(res.data);
      } else {
        const res = await authAxios.post(`/tables/${table.id}/split-bill`, { mode: "items", selected_items: selectedSplitItems });
        setSplitResult(res.data);
      }
    } catch { toast.error("Error al calcular"); }
  };

  const handleTransferItems = async () => {
    if (!targetTableId || selectedTransferItems.length === 0) { toast.error("Selecciona items y mesa destino"); return; }
    try {
      await authAxios.post(`/tables/${table.id}/transfer-items`, { target_table_id: targetTableId, item_indices: selectedTransferItems });
      toast.success("Items transferidos");
      setShowTransferItem(false);
      setSelectedTransferItems([]);
      setTargetTableId("");
      await Promise.all([fetchAccount(), onRefresh()]);
    } catch { toast.error("Error al transferir"); }
  };

  const handleTransferAll = async () => {
    if (!targetTableId) { toast.error("Selecciona mesa destino"); return; }
    try {
      await authAxios.post(`/tables/${table.id}/transfer-all`, { target_table_id: targetTableId });
      toast.success("Mesa transferida completa");
      setShowTransferAll(false);
      setTargetTableId("");
      await onRefresh();
      onBack();
    } catch { toast.error("Error al transferir"); }
  };

  const toggleSplitItem = (order_id, item_index) => {
    setSelectedSplitItems(prev => {
      const exists = prev.find(s => s.order_id === order_id && s.item_index === item_index);
      if (exists) return prev.filter(s => !(s.order_id === order_id && s.item_index === item_index));
      return [...prev, { order_id, item_index }];
    });
  };

  const toggleTransferItem = (order_id, item_index) => {
    setSelectedTransferItems(prev => {
      const exists = prev.find(s => s.order_id === order_id && s.item_index === item_index);
      if (exists) return prev.filter(s => !(s.order_id === order_id && s.item_index === item_index));
      return [...prev, { order_id, item_index }];
    });
  };

  const handleAddPartialPayment = () => {
    if (!paymentMethod) { toast.error(t("pos.selectPaymentMethod", "Seleccione un método de pago")); return; }
    if (!currentPartialAmount || partialAmount <= 0) { toast.error(t("pos.enterAmount", "Ingrese el monto")); return; }
    if (partialAmount > remainingToPay + 0.01) { toast.error(t("pos.amountExceedsRemaining", "El monto excede el pendiente")); return; }
    if (paymentMethod === "card" && !cardType) { toast.error(t("pos.selectCardType", "Seleccione el tipo de tarjeta")); return; }
    if (paymentMethod === "cash" && needsChange && (!cashReceived || parseFloat(cashReceived) < partialAmount)) {
      toast.error(t("pos.insufficientCash", "Valor recibido insuficiente")); return;
    }
    const partialReceived = paymentMethod === "cash" && !needsChange ? partialAmount : parseFloat(cashReceived) || partialAmount;
    const partialChange = paymentMethod === "cash" && needsChange ? partialCashChange : 0;
    setPartialPayments([...partialPayments, {
      id: Date.now(),
      amount: partialAmount,
      method: paymentMethod,
      card_type: paymentMethod === "card" ? cardType : null,
      change_amount: partialChange,
      cash_received: paymentMethod === "cash" ? partialReceived : null,
    }]);
    setCurrentPartialAmount("");
    setPaymentMethod("");
    setCardType("");
    setCashReceived("");
    toast.success(t("pos.partialPaymentAdded", "Pago parcial añadido") + `: ${formatPrice(partialAmount)}`);
  };

  const handleRemovePartialPayment = (paymentId) => {
    setPartialPayments(partialPayments.filter(p => p.id !== paymentId));
  };

  const resetPaymentState = () => {
    setShowPayment(false);
    setPaymentMode("single");
    setPaymentMethod("");
    setCardType("");
    setCashReceived("");
    setNeedsChange(false);
    setCurrentPartialAmount("");
    setPartialPayments([]);
    setAppliedDiscount(0);
  };

  const handlePayment = async () => {
    if (paymentMode === "single") {
      if (!paymentMethod) { toast.error(t("pos.selectPaymentMethod", "Seleccione un método de pago")); return; }
      if (paymentMethod === "cash" && needsChange && (!cashReceived || parseFloat(cashReceived) < finalTotal)) {
        toast.error(t("pos.insufficientCash", "Valor recibido insuficiente")); return;
      }
      if (paymentMethod === "card" && !cardType) { toast.error(t("pos.selectCardType", "Seleccione el tipo de tarjeta")); return; }
      // Pagamento exato: usar finalTotal como recebido, troco = 0
      const effectiveCashReceived = paymentMethod === "cash" && !needsChange ? finalTotal : parseFloat(cashReceived) || finalTotal;
      const effectiveChange = paymentMethod === "cash" && needsChange ? changeAmount : 0;
      setProcessingPayment(true);
      try {
        const resp = await submitTableSettlement(authAxios, table.id, finalTotal, appliedDiscount, paymentMethod, {
          card_type: paymentMethod === "card" ? cardType : null,
          change_amount: effectiveChange,
        });
        setLastPaymentResult(resp.data || {});
        const released = resp.data?.orders_released || 0;
        const releasedMsg = released > 0 ? ` • ${released} pedido(s) liberado(s) para cozinha` : "";
        toast.success(t("pos.paymentRegistered", "Pagamento registado") + `: ${formatPrice(finalTotal)}${releasedMsg}`);
        resetPaymentState();
        await Promise.all([fetchAccount(), onRefresh()]);
      } catch (e) {
        toast.error(toErrMsg(e.response?.data?.detail) || t("pos.paymentError", "Erro ao registar pagamento"));
      }
      setProcessingPayment(false);
    } else {
      if (partialPayments.length === 0) { toast.error(t("pos.addPartialPayments", "Adicione pelo menos um pagamento parcial")); return; }
      if (remainingToPay > 0.01) { toast.error(t("pos.completePayment", "Complete o pagamento total antes de confirmar")); return; }
      setProcessingPayment(true);
      try {
        const resp = await submitTableSettlement(authAxios, table.id, finalTotal, appliedDiscount, "combined", { payments: partialPayments });
        setLastPaymentResult(resp.data || {});
        toast.success(t("pos.combinedPaymentSuccess", "Pagamentos combinados registados") + `: ${formatPrice(finalTotal)}`);
        resetPaymentState();
        await Promise.all([fetchAccount(), onRefresh()]);
      } catch (e) {
        toast.error(toErrMsg(e.response?.data?.detail) || t("pos.paymentError", "Erro ao registar pagamentos"));
      }
      setProcessingPayment(false);
    }
  };

  const handlePrintReceipt = async () => {
    try {
      await authAxios.post(`/tables/${table.id}/print-receipt`);
      toast.success("Recibo enviado para impressão");
    } catch {
      // Fallback: open browser print dialog with current account data
      window.print();
    }
  };

  const handleApplyDiscount = () => {
    if (!discountValue || parseFloat(discountValue) <= 0) { toast.error("Informe o valor do desconto"); return; }
    const val = parseFloat(discountValue);
    let discountAmount = discountType === "percentage" ? (total * val) / 100 : val;
    if (discountAmount > total) { toast.error("Desconto não pode ser maior que o total"); return; }
    setAppliedDiscount(discountAmount);
    setShowDiscount(false);
    toast.success(`Desconto de ${formatPrice(discountAmount)} aplicado!`);
  };

  const handleEmitInvoice = async () => {
    if (orders.length === 0) { toast.error("Nenhum pedido para faturar"); return; }
    setProcessingInvoice(true);
    try {
      const orderId = orders[0]?.id;
      const res = await authAxios.post(`/vendus/invoice/${orderId}`);
      if (res.data.success) {
        toast.success(res.data.message || "Nota fiscal emitida com sucesso!");
        const pdfUrl = res.data.pdf_url || res.data.invoice_url;
        if (pdfUrl) window.open(pdfUrl, "_blank");
      } else {
        toast.error(res.data.error || "Erro ao emitir nota fiscal");
      }
    } catch (e) {
      toast.error(toErrMsg(e.response?.data?.detail) || "Erro ao conectar com sistema fiscal");
    }
    setProcessingInvoice(false);
  };

  const handleCloseTable = async () => {
    try {
      await authAxios.post(`/tables/${table.id}/close`);
      toast.success("Mesa encerrada! Pronta para próximo cliente.");
      await onRefresh();
      onBack();
    } catch (e) {
      toast.error(toErrMsg(e.response?.data?.detail) || "Erro ao encerrar mesa");
    }
  };

  return {
    // Dialog visibility
    showSplit, setShowSplit,
    showTransferItem, setShowTransferItem,
    showTransferAll, setShowTransferAll,
    showCancel, setShowCancel,
    showPayment, setShowPayment,
    showDiscount, setShowDiscount,
    showInvoice, setShowInvoice,
    // Split
    splitMode, setSplitMode, splitPersons, setSplitPersons, splitResult, setSplitResult, selectedSplitItems,
    // Transfer
    selectedTransferItems, setSelectedTransferItems, targetTableId, setTargetTableId,
    // Payment
    paymentMethod, setPaymentMethod, cardType, setCardType,
    cashReceived, setCashReceived, needsChange, setNeedsChange,
    paymentAmount, setPaymentAmount, processingPayment,
    paymentMode, setPaymentMode, partialPayments, currentPartialAmount, setCurrentPartialAmount,
    // Discount
    discountType, setDiscountType, discountValue, setDiscountValue, appliedDiscount, setAppliedDiscount,
    // Invoice
    processingInvoice,
    // Computed
    finalTotal, totalPartialPaid, remainingToPay, changeAmount, partialCashChange,
    // Handlers
    handleCancel, handleSplitCalc, handleTransferItems, handleTransferAll,
    toggleSplitItem, toggleTransferItem, handleAddPartialPayment, handleRemovePartialPayment,
    handlePayment, resetPaymentState, handleApplyDiscount, handleEmitInvoice, handleCloseTable,
    // Print receipt
    lastPaymentResult, setLastPaymentResult, handlePrintReceipt,
  };
}
